import { get, isArray, isEmpty, some, every } from 'lodash';
import { QuestionRule, RuleConditionItem, TypeName } from '../../services/model';

/**
 * 操作符枚举定义
 */
export const Operator = {
  /** 等于 */
  equal: 'Equal',
  /** 不等于 */
  notEqual: 'NotEqual',
  /** 包含任一 */
  containsAny: 'ContainsAny',
  /** 不包含 */
  notContains: 'NotContains',
  /** 为空 */
  isEmpty: 'IsEmpty',
  /** 不为空 */
  isNotEmpty: 'IsNotEmpty'
} as const;

export type OperatorType = typeof Operator[keyof typeof Operator];

/**
 * 表单数据类型定义
 */
export interface FormData {
  [key: string]: any;
}

/**
 * 问题类型映射
 */
export interface QuestionTypeMap {
  [key: string]: TypeName;
}

/**
 * 获取表单字段的值，处理数组情况
 */
function getFormFieldValue(formData: FormData, fieldKey: string, questionType: TypeName): any {
  const value = formData[fieldKey];

  if (!value) {
    return undefined;
  }

  // 处理数组情况，取第一个元素
  if (isArray(value) && value.length > 0) {
    const firstItem = value[0];

    // 根据问题类型处理数据
    switch (questionType) {
      case 'select':
      case 'multi_select':
        // 选择题返回 option_id
        // 如果 firstItem 是字符串，直接返回；如果是数组，提取第一个元素
        return typeof firstItem === 'string' ? firstItem : get(firstItem, '0');
      case 'five_star_scoring':
        // 评分题返回分数（转换为数字）
        // 如果 firstItem 是数字，直接返回；如果是数组，提取第一个元素
        return typeof firstItem === 'number' ? firstItem : (get(firstItem, '0') ? Number(get(firstItem, '0')) : undefined);
      case 'description':
      case 'questionnaire_description':
        // 描述题直接返回值
        return firstItem;
      default:
        return firstItem;
    }
  }

  return value;
}

/**
 * 获取多选题的所有选中选项
 */
function getMultiSelectValues(formData: FormData, fieldKey: string): string[] {
  const value = formData[fieldKey];

  if (!isArray(value) || value.length === 0) {
    return [];
  }

  return value.map((item: any) => get(item, '0')).filter(Boolean);
}

/**
 * 评估单个条件
 */
function evaluateCondition(
  condition: RuleConditionItem,
  formData: FormData,
  questionTypes: QuestionTypeMap,
  rule_list?: { [questionId: string]: QuestionRule | null | undefined },
  visited: Set<string> = new Set()
): boolean {
  const { left: fieldKey, operator, right } = condition;

  if (!fieldKey || !operator) {
    return false;
  }

  // 支持链式依赖校验
  if (rule_list && Object.prototype.hasOwnProperty.call(rule_list, fieldKey)) {
    if (visited.has(fieldKey)) {
      // 发现循环依赖，返回 false 以中断
      return false;
    }
    const newVisited = new Set(visited);
    newVisited.add(fieldKey);
    const isFieldVisible = interpretQuestionRule(
      rule_list[fieldKey],
      formData,
      questionTypes,
      rule_list,
      newVisited
    );
    if (!isFieldVisible) {
      return false;
    }
  }

  const questionType = questionTypes[fieldKey];
  const fieldValue = getFormFieldValue(formData, fieldKey, questionType);
  const expectedValues = right?.string_list || [];

  // 处理空值情况
  if (operator === Operator.isEmpty) {
    if (typeof fieldValue === 'number') {
      return false;
    }
    return isEmpty(fieldValue);
  }

  if (operator === Operator.isNotEmpty) {
    if (typeof fieldValue === 'number') {
      return true;
    }
    return !isEmpty(fieldValue);
  }

  // 对于多选题，需要特殊处理
  if (questionType === 'multi_select') {
    const selectedValues = getMultiSelectValues(formData, fieldKey);

    switch (operator) {
      case Operator.equal:
        return expectedValues.length === 1 && selectedValues.includes(expectedValues[0]);

      case Operator.notEqual:
        return expectedValues.length === 1 && !selectedValues.includes(expectedValues[0]);

      case Operator.containsAny:
        return some(expectedValues, (expectedValue) => selectedValues.includes(expectedValue));

      case Operator.notContains:
        return !some(expectedValues, (expectedValue) => selectedValues.includes(expectedValue));

      default:
        return false;
    }
  }

  // 处理评分题（1-5数字匹配）
  if (questionType === 'five_star_scoring' && typeof fieldValue === 'number') {
    const score = fieldValue;

    switch (operator) {
      case Operator.equal:
        return expectedValues.some((expected) => Number(expected) === score);

      case Operator.notEqual:
        return !expectedValues.some((expected) => Number(expected) === score);

      case Operator.containsAny:
        return expectedValues.some((expected) => Number(expected) === score);

      case Operator.notContains:
        return !expectedValues.some((expected) => Number(expected) === score);

      default:
        return false;
    }
  }

  // 处理单选题和描述题
  const actualValue = String(fieldValue || '');

  switch (operator) {
    case Operator.equal:
      return expectedValues.some((expected) => String(expected) === actualValue);

    case Operator.notEqual:
      return !expectedValues.some((expected) => String(expected) === actualValue);

    case Operator.containsAny:
      return some(expectedValues, (expected) => actualValue.includes(String(expected)));

    case Operator.notContains:
      return !some(expectedValues, (expected) => actualValue.includes(String(expected)));

    default:
      return false;
  }
}

/**
 * 解析逻辑表达式
 * 支持简单的 and/or 逻辑，如 "1 and 2" 或 "1 or 2"
 */
function parseLogicExpression(
  logic: string,
  conditions: RuleConditionItem[],
  formData: FormData,
  questionTypes: QuestionTypeMap,
  rule_list?: { [questionId: string]: QuestionRule | null | undefined },
  visited: Set<string> = new Set()
): boolean {
  if (!logic || !conditions || conditions.length === 0) {
    return true;
  }

  // 清理逻辑表达式
  const cleanLogic = logic.trim().toLowerCase();

  // 简单的 and/or 解析
  if (cleanLogic.includes('and')) {
    const conditionIndices = cleanLogic.split('and').map((s) => parseInt(s.trim(), 10));
    return every(conditionIndices, (index) => {
      if (index < 1 || index > conditions.length) return false;
      return evaluateCondition(conditions[index - 1], formData, questionTypes, rule_list, visited);
    });
  }

  if (cleanLogic.includes('or')) {
    const conditionIndices = cleanLogic.split('or').map((s) => parseInt(s.trim(), 10));
    return some(conditionIndices, (index) => {
      if (index < 1 || index > conditions.length) return false;
      return evaluateCondition(conditions[index - 1], formData, questionTypes, rule_list, visited);
    });
  }

  // 单个条件的情况
  const singleIndex = parseInt(cleanLogic, 10);
  if (!isNaN(singleIndex) && singleIndex >= 1 && singleIndex <= conditions.length) {
    return evaluateCondition(conditions[singleIndex - 1], formData, questionTypes, rule_list, visited);
  }

  // 默认返回 true（显示问题）
  return true;
}

/**
 * 主要的规则解释器函数
 * 根据表单数据和问题类型判断问题是否应该显示
 */
export function interpretQuestionRule(
  rule: QuestionRule | null | undefined,
  formData: FormData,
  questionTypes: QuestionTypeMap,
  rule_list?: { [questionId: string]: QuestionRule | null | undefined },
  visited: Set<string> = new Set()
): boolean {
  if (!rule) {
    return true; // 没有规则，默认显示
  }

  const { logic, conditions } = rule;

  if (!conditions || conditions.length === 0) {
    return true; // 没有条件，默认显示
  }

  return parseLogicExpression(
    logic || '1',
    conditions,
    formData,
    questionTypes,
    rule_list,
    visited
  );
}

/**
 * 批量解释多个问题的规则
 */
export function interpretMultipleQuestionRules(
  rules: { [questionId: string]: QuestionRule | null | undefined },
  formData: FormData,
  questionTypes: QuestionTypeMap
): { [questionId: string]: boolean } {
  const results: { [questionId: string]: boolean } = {};

  Object.keys(rules).forEach((questionId) => {
    results[questionId] = interpretQuestionRule(rules[questionId], formData, questionTypes, rules);
  });

  return results;
}

export default {
  interpretQuestionRule,
  interpretMultipleQuestionRules,
  evaluateCondition,
  parseLogicExpression,
  Operator
};
